import { useCallback, useMemo } from "react";

import toast from "react-hot-toast";
import { useParams } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import type {
  ChallengeDetail,
  ChallengeSettingsUpdate,
  UseChallengeQueryReturn,
} from "../types";

type UseChallengeQueryOptions = { challengeId?: string };

export function useChallengeQuery(
  options: UseChallengeQueryOptions = {},
): UseChallengeQueryReturn {
  const params = useParams<{ id: string }>();
  const challengeId = options.challengeId ?? params.id;
  const user = useCurrentUser();

  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [challengeId ?? ""], {
      isEnabled: !!challengeId,
      refetchInterval: (query) =>
        query.state.data?.assessmentScoreProcessing ? 60000 : false,
    }),
  );
  const challenge = challengeQuery.data;

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );

  const updateChallenge = useCallback(
    (
      update: ChallengeSettingsUpdate,
      onSuccess?: (updated: ChallengeDetail) => void,
      onError?: (err: { message: string }) => void,
    ) => {
      if (!challengeId) return;
      api.challenges.update(
        challengeId,
        update,
        (updatedChallenge) => {
          api.setQueryData(api.challenges.get, [challengeId], updatedChallenge);
          onSuccess?.(updatedChallenge);
        },
        (err) => {
          if (onError) {
            onError(err);
          } else {
            toast.error(err.message);
          }
        },
      );
    },
    [challengeId],
  );

  const updateChallengeField = useCallback(
    <K extends keyof ChallengeSettingsUpdate>(
      key: K,
      value: ChallengeSettingsUpdate[K],
    ) => {
      updateChallenge({ [key]: value } as ChallengeSettingsUpdate);
    },
    [updateChallenge],
  );

  return {
    challengeId,
    challenge,
    challengeQuery,
    user,
    canManageChallenge,
    updateChallenge,
    updateChallengeField,
  };
}
